import "./CustomFileInput.scss";

/**
 * Polyfills Bootstrap 4's `.custom-file-input` / `.custom-file-label`
 * behaviour in a Bootstrap 5 project.
 *
 * When a file is selected the label text updates to show the chosen
 * filename(s), matching the UX that Bootstrap 4 provided via its
 * built-in jQuery plugin.
 *
 * Usage:
 *   import { CustomFileInput } from "./CustomFileInput";
 *   new CustomFileInput();
 *
 * HTML structure expected:
 *   <div class="custom-file">
 *     <input type="file" class="custom-file-input" id="myFile" />
 *     <label class="custom-file-label" for="myFile">Choose file</label>
 *   </div>
 */
export class CustomFileInput
{
	private static readonly INPUT_SELECTOR = ".custom-file-input";
	private static readonly LABEL_SELECTOR = ".custom-file-label";
	private static readonly DEFAULT_LABEL = "Choose file";

	constructor(root: ParentNode = document)
	{
		this.Bind(root);
	}

	/**
	 * Bind change listeners to all `.custom-file-input` elements found
	 * inside `root`.  Safe to call again after dynamic DOM insertion.
	 */
	public Bind = (root: ParentNode = document): void =>
	{
		const inputs = root.querySelectorAll<HTMLInputElement>(CustomFileInput.INPUT_SELECTOR);

		inputs.forEach(input =>
		{
			// Avoid double-binding
			if (input.dataset.customFileInit === "true") { return; }

			input.dataset.customFileInit = "true";
			input.addEventListener("change", this.OnInputChange);
		});
	};

	/**
	 * Reset all custom-file labels to their default placeholder text.
	 */
	public Reset = (root: ParentNode = document): void =>
	{
		const inputs = root.querySelectorAll<HTMLInputElement>(CustomFileInput.INPUT_SELECTOR);

		inputs.forEach(input =>
		{
			const label = CustomFileInput.ResolveLabel(input);
			if (label)
			{
				label.textContent = label.dataset.defaultText ?? CustomFileInput.DEFAULT_LABEL;
			}
		});
	};

	// ------------------------------------------------------------------ //
	//  Private helpers
	// ------------------------------------------------------------------ //

	private OnInputChange = (event: Event): void =>
	{
		const input = event.target as HTMLInputElement;
		const label = CustomFileInput.ResolveLabel(input);

		if (!label) { return; }

		// Persist the original placeholder so reset() can restore it
		if (!label.dataset.defaultText)
		{
			label.dataset.defaultText = label.textContent ?? CustomFileInput.DEFAULT_LABEL;
		}

		label.textContent = CustomFileInput.BuildLabelText(input, label);
	};

	/**
	 * Find the `<label>` associated with the given input element.
	 *
	 * Tries (in order):
	 *  1. A sibling `.custom-file-label` within the same `.custom-file` wrapper
	 *  2. A `<label[for="<inputId>"]>` anywhere in the document
	 */
	private static ResolveLabel(input: HTMLInputElement): HTMLLabelElement | null
	{
		// Prefer sibling label inside the same wrapper
		const wrapper = input.closest<HTMLElement>(".custom-file");
		if (wrapper)
		{
			const sibling = wrapper.querySelector<HTMLLabelElement>(CustomFileInput.LABEL_SELECTOR);
			if (sibling) { return sibling; }
		}

		// Fall back to explicit for/id association
		if (input.id)
		{
			return document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(input.id)}"]`);
		}

		return null;
	}

	/**
	 * Build a human-readable string from the selected `FileList`.
	 *
	 * - No files     → default placeholder
	 * - Single file  → filename
	 * - Many files   → "3 files selected" (or custom message via
	 *                  `data-multiple-caption` on the input element)
	 */
	private static BuildLabelText(input: HTMLInputElement, label: HTMLLabelElement): string
	{
		const files = input.files;

		if (!files || files.length === 0)
		{
			return label.dataset.defaultText ?? CustomFileInput.DEFAULT_LABEL;
		}

		if (files.length === 1)
		{
			return files[0].name;
		}

		const template = input.dataset.multipleCaption ?? "{count} files selected";
		return template.replace("{count}", String(files.length));
	}
}
